/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1697673600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Oct 19 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "greenplum",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Choose connection type",
          "depth": 1,
          "id": "choose-connection-type"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Provide credentials",
          "depth": 1,
          "id": "provide-credentials"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {ConnectionTypeSnippet, SyncEngineSnippet, EngineComparisonTable, SourceTestSnippet, NoCommonErrors} = _components;
    if (!ConnectionTypeSnippet) _missingMdxReference("ConnectionTypeSnippet", true);
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in your Greenplum database and push it to downstream destinations. Most of the setup occurs in the Hightouch UI, but you need access to your Greenplum instance for information like your host, port, database name, and credentials."
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Greenplum Database"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-connection-type",
        children: "Choose connection type"
      }), "\n", _jsx(ConnectionTypeSnippet, {
        source: "Greenplum"
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Greenplum server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Greenplum server. The default port number is 5432, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": This specifies the database to use when Hightouch executes queries in Greenplum."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Greenplum"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also supports more features, such as ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Greenplum"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsx(_components.p, {
        children: "To set up the Lightning engine, you need to grant Hightouch write access to Greenplum. You can do so by running the following SQL snippet."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "USER"
          }), " hightouch_user ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " PASSWORD ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'********'"
          }), ";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_audit;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " hightouch_planner;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), ", USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_audit ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " hightouch_user;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), ", USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA hightouch_planner ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " hightouch_user;\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The snippet creates a dedicated Greenplum user for Hightouch. It also provisions two schemas (", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), ") for storing logs of previously synced data."]
      }), "\n", _jsx(_components.h3, {
        id: "provide-credentials",
        children: "Provide credentials"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": This can be your personal Greenplum login or a dedicated user for Hightouch. At minimum, this user must have read access to the data you wish to sync. If using the Lightning sync engine, you must also grant this user additional permissions as described above."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Greenplum"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Greenplum."]
      }), "\n", _jsx(_components.p, {
        children: "The Greenplum source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/looker-models",
            children: "Looker Looks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/sigma",
            children: "Sigma workbooks"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(NoCommonErrors, {
        source: "true"
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
